/**
 * The alert review pane: evidence, operational facts, decisions, history.
 *
 * It loads CANONICAL detail from `/api/alerts/{id}/` rather than reading the
 * list row it was opened from. A queue or history row is a summary projection —
 * it can be minutes stale and carries only a subset of the evidence and workflow
 * fields, and an operator deciding from a stale status is the failure this
 * separation exists to prevent. The row is used only to fill the header while
 * the real record loads, so the pane is never blank.
 *
 * A detail failure is contained here: the surrounding queue or history list, its
 * filters and its pagination all survive it.
 */
import { useTranslation } from "react-i18next";
import { ChevronDown, ExternalLink, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/ui/components/ui/button";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { cn } from "@/ui/lib/utils";
import {
  useAlertActivities,
  useAlertWorkspaceDetail,
} from "@/alerts/api/queries/alerts-workspace.queries";
import { alertWorkflowFailure } from "@/alerts/api/envelope";
import { alertStatusLabel, alertTypeLabel } from "@/alerts/types/machine-alert";
import { readDispositionLabel } from "@/alerts/utils/dispositions";
import { formatSiteTime, waitedFor } from "@/alerts/utils/format";
import { AlertActionBar } from "./alert-action-bar";
import { AlertActivityTimeline } from "./alert-activity-timeline";
import { AlertEvidenceViewer } from "./alert-evidence-viewer";
import type { AlertQueueRow, AlertRow } from "@/alerts/types/alerts-workspace";
import { AlertStatus } from "@/alerts/types/machine-alert";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="border-t border-border px-4 py-3">
    <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
    {children}
  </section>
);

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <div className="truncate text-sm">{value}</div>
  </div>
);

export const AlertDetailPane = ({
  row,
  onClose,
  onNext,
  asOf,
}: {
  /** The list row that was selected — header seed only, never the source of truth. */
  row: AlertRow;
  onClose: () => void;
  onNext?: () => void;
  asOf?: string;
}) => {
  const { t } = useTranslation("alerts");
  const detailQuery = useAlertWorkspaceDetail(row.id);
  const activitiesQuery = useAlertActivities(row.id);
  const detail = detailQuery.data;

  // Canonical values where we have them, the row's as a stopgap while loading.
  const status = detail?.status ?? row.status;
  const timezone = detail?.location_timezone ?? row.location_timezone;
  const disposition = detail?.disposition ?? row.disposition;
  const legacyDisposition =
    detail?.disposition_legacy_defaulted ?? row.disposition_legacy_defaulted;
  const queueRow = (row as AlertQueueRow).waiting_since ? (row as AlertQueueRow) : null;

  // `preview` is ONE frame, not the collection — only a fallback when the
  // canonical `images` array is absent.
  const frames = detail?.images?.length ? detail.images : detail?.preview ? [detail.preview] : [];

  const notFound =
    detailQuery.isError && alertWorkflowFailure(detailQuery.error) === "alert_not_found";

  return (
    <div className="flex min-h-0 flex-col">
      <header className="sticky top-0 z-10 flex items-start justify-between gap-2 border-b border-border bg-background px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">{alertTypeLabel(row.type)}</h2>
          <p className="truncate text-xs text-muted-foreground">
            #{row.id} · {row.machine_name || row.device_mxid} · {row.location_name}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onNext && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-xs"
              onClick={onNext}
              title={t("detail.nextTitle")}
            >
              {t("detail.next")}
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            aria-label={t("detail.close")}
            className="h-7 w-7"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {notFound ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm font-medium">{t("detail.notFound.title")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("detail.notFound.hint")}</p>
          <Button size="sm" variant="outline" className="mt-3 h-7 text-xs" onClick={onClose}>
            {t("detail.notFound.back")}
          </Button>
        </div>
      ) : (
        <>
          <Section title={t("detail.sections.decision")}>
            <AlertActionBar
              alertId={row.id}
              // Never rendered — a concurrency token, not an operator-facing fact.
              workflowVersion={detail?.workflow_version ?? null}
              // A correction flips the CURRENT result, so the target is the
              // opposite of what the alert carries now. Read from canonical
              // detail, never from the row.
              correctionTarget={
                detail?.status === AlertStatus.Resolved ? "closed" : "confirmed_signal"
              }
              // Which KIND of action this alert takes at all — a first decision
              // or a correction. From canonical detail, never from the row.
              currentStatus={detail?.status ?? null}
              // Acting before canonical detail lands would send a stale (or no)
              // version and could overwrite a concurrent decision.
              disabled={detailQuery.isPending}
              onActionApplied={onNext}
            />
          </Section>

          <Section title={t("detail.sections.evidence")}>
            {detailQuery.isPending ? (
              <Skeleton className="h-40 w-full" />
            ) : detailQuery.isError ? (
              <p className="text-xs text-muted-foreground">{t("detail.evidenceError")}</p>
            ) : (
              <AlertEvidenceViewer frames={frames} video={detail?.video} timezone={timezone} />
            )}
          </Section>

          <Section title={t("detail.sections.facts")}>
            <div className="grid grid-cols-2 gap-3">
              <Fact label={t("detail.facts.status")} value={alertStatusLabel(status)} />
              <Fact
                label={t("detail.facts.detected")}
                value={formatSiteTime(row.timestamp, timezone)}
              />
              <Fact label={t("detail.facts.machine")} value={row.machine_name || row.device_mxid} />
              <Fact
                label={t("detail.facts.device")}
                value={<span className="font-mono text-xs">{row.device_mxid}</span>}
              />
              <Fact label={t("detail.facts.eventState")} value={row.event_state || "—"} />
              <Fact
                label={t("detail.facts.reviewCycle")}
                value={detail?.review_cycle ?? row.review_cycle}
              />
              {queueRow && (
                <Fact
                  label={t("detail.facts.waiting")}
                  value={`${queueRow.waiting_since_estimated ? "~" : ""}${waitedFor(
                    queueRow.waiting_since ?? row.platform_received_at ?? row.timestamp,
                    asOf,
                  )}`}
                />
              )}
              {(detail?.closed_at ?? row.closed_at) && (
                <Fact
                  label={t("detail.facts.closed")}
                  value={formatSiteTime(detail?.closed_at ?? row.closed_at, timezone)}
                />
              )}
              {disposition && (
                <Fact
                  label={t("detail.facts.reason")}
                  value={
                    <span
                      className={cn(
                        legacyDisposition && "underline decoration-dotted underline-offset-2",
                      )}
                      // A legacy-defaulted reason was derived, not chosen — it
                      // must not read as a decision an operator actually made.
                      title={legacyDisposition ? t("detail.facts.legacyTitle") : undefined}
                    >
                      {readDispositionLabel(disposition)}
                      {legacyDisposition ? ` ${t("detail.facts.provisional")}` : ""}
                    </span>
                  }
                />
              )}
              <Fact
                label={t("detail.facts.frames")}
                value={detail?.image_count ?? row.image_count}
              />
            </div>
          </Section>

          <Section title={t("detail.sections.context")}>
            <div className="flex flex-wrap gap-2">
              {/* The event link must work with no truck cycle — an empty cycle
                  segment would produce a URL that resolves to nothing. */}
              <Link
                to={`/machine/event/${row.event_uuid}`}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
              >
                <ExternalLink className="h-3 w-3" aria-hidden />
                {t("detail.context.event")}
              </Link>
              {detail?.truck_cycle_id && (
                <Link
                  to={`/machine/cycle/${detail.truck_cycle_id}`}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                >
                  <ExternalLink className="h-3 w-3" aria-hidden />
                  {t("detail.context.truckCycle")}
                </Link>
              )}
            </div>
          </Section>

          {!!detail?.related_alerts?.count && (
            <Section title={t("detail.sections.related")}>
              {/* Comparison context only. Nothing here closes or classifies an
                  alert, and `related_alert_id` is never sent with an action. */}
              <ul className="space-y-1">
                {detail.related_alerts.items.map((related) => (
                  <li key={related.id} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-muted-foreground">#{related.id}</span>
                    <span className="truncate">{alertTypeLabel(related.type)}</span>
                    <span className="ml-auto shrink-0 text-muted-foreground">
                      {formatSiteTime(related.timestamp, timezone)}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title={t("detail.sections.activity")}>
            <AlertActivityTimeline
              activities={activitiesQuery.data ?? []}
              isLoading={activitiesQuery.isPending}
              isError={activitiesQuery.isError}
              timezone={timezone}
            />
          </Section>
        </>
      )}
    </div>
  );
};
